// src/screens/GameDetailScreen.js
// ============================================================================
// GAME DETAIL SCREEN
//
// Displays comprehensive game information:
// - Hero cover image with game title
// - Screenshots gallery with horizontal scroll
// - Description, genres, platforms
// - System requirements (min/recommended)
// - Competition formats available
// - Reviews/ratings
// - Add to Library / Download / Play button
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  SafeAreaView,
  Image,
  Dimensions,
  Platform,
  Linking,
} from 'react-native';
import { httpsCallable } from 'firebase/functions';
import {
  getFirestore, doc, getDoc, collection, getDocs,
} from 'firebase/firestore';
import { auth, functions } from '../../firebaseConfig';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useError } from '../components/ErrorContext';
import Header from '../components/Header';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import {
  isDesktopApp,
  getPlatformInfo,
  getInstalledGames,
  launchGame,
  downloadGame,
  installGame,
} from '../utils/platform';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const db = getFirestore();

// Integration level labels (using Ionicons names instead of emojis)
const INTEGRATION_LABELS = {
  0: { label: 'Vision Verified', color: '#9333ea', iconName: 'tv-outline' },
  1: { label: 'API Integrated', color: '#3b82f6', iconName: 'link-outline' },
  2: { label: 'Full Matchmaking', color: '#22c55e', iconName: 'game-controller-outline' },
  3: { label: 'Native Integration', color: '#FCD000', iconName: 'star' },
};

export default function GameDetailScreen({ navigation, route }) {
  const { gameId } = route.params || {};
  
  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState(null);
  const [owned, setOwned] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [installedVersion, setInstalledVersion] = useState(null);
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  const [addingToLibrary, setAddingToLibrary] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadState, setDownloadState] = useState('idle'); // idle, downloading, installing
  const { showError } = useError();

  const inDesktopClient = isDesktopApp();

  // Load game details on mount
  useEffect(() => {
    if (gameId) {
      loadGameDetails();
    } else {
      showError('No game specified');
      if (navigation.canGoBack()) navigation.goBack(); else navigation.navigate('Lobby');
    }
  }, [gameId]);

  // Check local install status
  useEffect(() => {
    if (inDesktopClient && owned) {
      checkLocalInstall();
    }
  }, [inDesktopClient, owned]);

  const loadGameDetails = async () => {
    setLoading(true);
    try {
      let gameData = null;

      // Try cloud function first
      try {
        const getGameDetails = httpsCallable(functions, 'getGameDetails');
        const result = await getGameDetails({ gameId });
        gameData = result.data;
      } catch (fnErr) {
        console.log('[GameDetail] getGameDetails unavailable, trying Firestore:', fnErr.message);
      }

      // Firestore fallback - read game_catalog directly
      if (!gameData) {
        try {
          const docSnap = await getDoc(doc(db, 'game_catalog', gameId));
          if (docSnap.exists()) {
            gameData = { id: docSnap.id, gameId: docSnap.id, ...docSnap.data() };
            // Check if user owns the game
            if (auth.currentUser) {
              try {
                const libDoc = await getDoc(
                  doc(db, 'users', auth.currentUser.uid, 'game_library', gameId)
                );
                gameData.owned = libDoc.exists();
              } catch (libErr) {
                console.log('[GameDetail] Library check failed:', libErr.message);
              }
            }
          }
        } catch (fsErr) {
          console.log('[GameDetail] Firestore fallback failed:', fsErr.message);
        }
      }

      if (gameData) {
        setGame(gameData);
        setOwned(gameData.owned || false);
      } else {
        showError('Game not found');
      }
    } catch (err) {
      console.error('[GameDetail] Failed to load game:', err);
      showError('Failed to load game details');
    } finally {
      setLoading(false);
    }
  };

  const checkLocalInstall = async () => {
    try {
      const installedGames = await getInstalledGames();
      const gameInfo = installedGames[gameId];
      if (gameInfo?.installed) {
        setInstalled(true);
        setInstalledVersion(gameInfo.version);
      }
    } catch (err) {
      console.error('[GameDetail] Failed to check local install:', err);
    }
  };

  // Handle adding game to library
  const handleAddToLibrary = async () => {
    if (!auth.currentUser) {
      navigation.navigate('Login');
      return;
    }

    setAddingToLibrary(true);
    try {
      const addToLibrary = httpsCallable(functions, 'addGameToLibrary');
      await addToLibrary({ gameId });
      setOwned(true);
    } catch (err) {
      console.error('[GameDetail] Failed to add to library:', err);
      showError(err.message || 'Failed to add game to library');
    } finally {
      setAddingToLibrary(false);
    }
  };

  // Handle download
  const handleDownload = async () => {
    if (!inDesktopClient) {
      navigation.navigate('Download');
      return;
    }

    const platformInfo = await getPlatformInfo();
    const platform = platformInfo?.platform || 'win32';
    const downloadUrl = game.downloadUrls?.[platform];

    if (!downloadUrl) {
      showError(`No download available for ${platform}`);
      return;
    }

    setDownloadState('downloading');
    setDownloadProgress(0);

    const result = await downloadGame(gameId, downloadUrl, (progress) => {
      setDownloadProgress(progress);
    });

    if (result.success) {
      setDownloadState('installing');
      const installResult = await installGame(gameId);
      
      if (installResult.success) {
        setInstalled(true);
        checkLocalInstall();
      } else {
        showError(installResult.error || 'Installation failed');
      }
    } else {
      showError(result.error || 'Download failed');
    }
    
    setDownloadState('idle');
    setDownloadProgress(0);
  };

  // Handle play button
  const handlePlay = async () => {
    if (!inDesktopClient) {
      navigation.navigate('Download');
      return;
    }

    const result = await launchGame(gameId);
    if (!result.success) {
      showError(result.error || 'Failed to launch game');
    }
  };

  // Render loading state
  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <Header navigation={navigation} />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#FCD000" />
          <Text style={styles.loadingText}>Loading game details...</Text>
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  // Render error state if no game
  if (!game) {
    return (
      <SafeAreaView style={styles.container}>
        <Header navigation={navigation} />
        <View style={styles.errorContainer}>
          <Ionicons name="sad-outline" size={48} color="#666" />
          <Text style={styles.errorTitle}>Game not found</Text>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.navigate('Store')}
          >
            <Text style={styles.backButtonText}>Back to Store</Text>
          </TouchableOpacity>
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  const integrationInfo = INTEGRATION_LABELS[game.integrationLevel] || INTEGRATION_LABELS[0];
  const needsUpdate = installed && installedVersion && installedVersion !== game.currentVersion;

  return (
    <SafeAreaView style={styles.container}>
      <Header navigation={navigation} />
      
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.content}>
        {/* Back Button */}
        <TouchableOpacity
          style={styles.backLink}
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}
        >
          <Text style={styles.backLinkText}>Back to Store</Text>
        </TouchableOpacity>

        {/* Hero Section */}
        <View style={styles.heroSection}>
          {/* Cover Image */}
          <View style={styles.coverContainer}>
            {game.coverImage ? (
              <Image source={{ uri: game.coverImage }} style={styles.coverImage} />
            ) : (
              <View style={styles.placeholderCover}>
                <Text style={styles.placeholderText}>{game.name?.[0] || '?'}</Text>
              </View>
            )}
            
            {/* Integration Badge */}
            <View style={[styles.integrationBadge, { backgroundColor: integrationInfo.color }]}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Ionicons name={integrationInfo.iconName} size={12} color="#fff" style={{ marginRight: 4 }} />
                <Text style={styles.integrationBadgeText}>
                  {integrationInfo.label}
                </Text>
              </View>
            </View>

            {/* First Party Banner */}
            {game.category === 'first_party' && (
              <View style={styles.firstPartyBanner}>
                <Text style={styles.firstPartyText}><Ionicons name="star" size={12} color="#FCD000" /> CritCash Original</Text>
              </View>
            )}
          </View>

          {/* Title and Meta */}
          <View style={styles.heroInfo}>
            <Text style={styles.gameTitle}>{game.name}</Text>
            <Text style={styles.developer}>{game.developer || 'CritCash'}</Text>
            
            {/* Stats Row */}
            <View style={styles.statsRow}>
              {game.stats?.activeUsers > 0 && (
                <View style={styles.statItem}>
                  <Text style={styles.statValue}>{game.stats.activeUsers.toLocaleString()}</Text>
                  <Text style={styles.statLabel}>Players</Text>
                </View>
              )}
              {game.priceCents === 0 && (
                <View style={styles.statItem}>
                  <Text style={[styles.statValue, styles.freeLabel]}>FREE</Text>
                  <Text style={styles.statLabel}>to play</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Action Section */}
        <View style={styles.actionSection}>
          {!owned ? (
            // Not owned - Show "Add to Library" button
            <TouchableOpacity
              style={[styles.primaryButton, addingToLibrary && styles.buttonDisabled]}
              onPress={handleAddToLibrary}
              disabled={addingToLibrary}
            >
              {addingToLibrary ? (
                <ActivityIndicator size="small" color="#000" />
              ) : (
                <Text style={styles.primaryButtonText}>
                  {game.priceCents ? `Get for $${(game.priceCents / 100).toFixed(2)}` : '+ Add to Library'}
                </Text>
              )}
            </TouchableOpacity>
          ) : installed && !needsUpdate ? (
            // Installed - Show "Play" button
            <TouchableOpacity
              style={[styles.primaryButton, styles.playButton]}
              onPress={handlePlay}
            >
              <Text style={styles.primaryButtonText}><Ionicons name="play" size={14} color="#000" /> Play Now</Text>
            </TouchableOpacity>
          ) : downloadState === 'downloading' ? (
            // Downloading
            <View style={styles.downloadingContainer}>
              <View style={styles.progressBar}>
                <View style={[styles.progressFill, { width: `${downloadProgress}%` }]} />
              </View>
              <Text style={styles.progressText}>Downloading... {Math.round(downloadProgress)}%</Text>
            </View>
          ) : downloadState === 'installing' ? (
            // Installing
            <View style={[styles.primaryButton, styles.installingButton]}>
              <ActivityIndicator size="small" color="#000" />
              <Text style={[styles.primaryButtonText, { marginLeft: 8 }]}>Installing...</Text>
            </View>
          ) : (
            // Owned but not installed - Show download button
            <TouchableOpacity
              style={[styles.primaryButton, styles.downloadButtonStyle]}
              onPress={handleDownload}
            >
              <Text style={[styles.primaryButtonText, { color: '#fff' }]}>
                {needsUpdate ? 'Update' : 'Download'}
              </Text>
            </TouchableOpacity>
          )}

          {/* Secondary Actions */}
          <View style={styles.secondaryActions}>
            {owned && (
              <TouchableOpacity
                style={styles.secondaryButton}
                onPress={() => navigation.navigate('GameLibrary')}
              >
                <Text style={styles.secondaryButtonText}>View in Library</Text>
              </TouchableOpacity>
            )}
            {!inDesktopClient && (
              <TouchableOpacity
                style={styles.secondaryButton}
                onPress={() => navigation.navigate('Download')}
              >
                <Text style={styles.secondaryButtonText}><Ionicons name="desktop-outline" size={14} color="#ccc" /> Get Desktop App</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Screenshots Gallery */}
        {game.screenshots?.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Screenshots</Text>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              pagingEnabled
              onMomentumScrollEnd={(e) => {
                const index = Math.round(e.nativeEvent.contentOffset.x / (SCREEN_WIDTH - 40));
                setActiveScreenshot(index);
              }}
              contentContainerStyle={styles.screenshotContainer}
            >
              {game.screenshots.map((url, index) => (
                <Image
                  key={index}
                  source={{ uri: url }}
                  style={styles.screenshot}
                  resizeMode="cover"
                />
              ))}
            </ScrollView>
            {/* Dots indicator */}
            {game.screenshots.length > 1 && (
              <View style={styles.dotsContainer}>
                {game.screenshots.map((_, index) => (
                  <View
                    key={index}
                    style={[
                      styles.dot,
                      activeScreenshot === index && styles.dotActive,
                    ]}
                  />
                ))}
              </View>
            )}
          </View>
        )}

        {/* Description */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>About This Game</Text>
          <Text style={styles.description}>{game.description}</Text>
        </View>

        {/* Genres & Platforms */}
        <View style={styles.section}>
          <View style={styles.metaGrid}>
            {/* Genres */}
            {game.genres?.length > 0 && (
              <View style={styles.metaItem}>
                <Text style={styles.metaLabel}>Genres</Text>
                <View style={styles.tagContainer}>
                  {game.genres.map((genre, i) => (
                    <View key={i} style={styles.tag}>
                      <Text style={styles.tagText}>{genre}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Platforms */}
            {game.platforms?.length > 0 && (
              <View style={styles.metaItem}>
                <Text style={styles.metaLabel}>Platforms</Text>
                <View style={styles.tagContainer}>
                  {game.platforms.map((platform, i) => (
                    <View key={i} style={[styles.tag, styles.platformTag]}>
                      <Text style={styles.tagText}>
                        {platform === 'Windows' && <Ionicons name="logo-windows" size={12} color="#ccc" />}
                        {platform === 'macOS' && <Ionicons name="logo-apple" size={12} color="#ccc" />}
                        {platform !== 'Windows' && platform !== 'macOS' && <Ionicons name="logo-tux" size={12} color="#ccc" />}
                        {' '}{platform}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Version */}
            <View style={styles.metaItem}>
              <Text style={styles.metaLabel}>Version</Text>
              <Text style={styles.metaValue}>{game.currentVersion || '1.0.0'}</Text>
            </View>
          </View>
        </View>

        {/* Competition Formats */}
        {game.competitionConfig?.formats && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}><Ionicons name="trophy" size={16} color="#FCD000" /> Competition Formats</Text>
            <Text style={styles.sectionSubtitle}>
              {game.competitionConfig.streamRequired === false
                ? 'No streaming required - native integration'
                : 'Stream verification required'}
            </Text>
            <View style={styles.formatsGrid}>
              {Object.entries(game.competitionConfig.formats).map(([name, format]) => (
                <View key={name} style={styles.formatCard}>
                  <Text style={styles.formatName}>{name}</Text>
                  <Text style={styles.formatDescription}>{format.description}</Text>
                  <View style={styles.formatMeta}>
                    <Text style={styles.formatMetaText}>
                      <Ionicons name="people" size={12} color="#999" /> {format.teamSize === 1 ? 'Solo' : `${format.teamSize}v${format.teamSize}`}
                    </Text>
                    <Text style={styles.formatMetaText}>
                      <Ionicons name="time-outline" size={12} color="#999" /> ~{format.durationMinutes}min
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* System Requirements */}
        {game.requirements && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}><Ionicons name="desktop-outline" size={16} color="#FCD000" /> System Requirements</Text>
            <View style={styles.requirementsTable}>
              <View style={styles.requirementsHeader}>
                <Text style={styles.requirementsHeaderText}>Minimum</Text>
              </View>
              {game.requirements.minimum && Object.entries(game.requirements.minimum).map(([key, value]) => (
                <View key={key} style={styles.requirementRow}>
                  <Text style={styles.requirementLabel}>{key.toUpperCase()}</Text>
                  <Text style={styles.requirementValue}>{value}</Text>
                </View>
              ))}
              {game.requirements.recommended && (
                <>
                  <View style={[styles.requirementsHeader, { marginTop: 16 }]}>
                    <Text style={styles.requirementsHeaderText}>Recommended</Text>
                  </View>
                  {Object.entries(game.requirements.recommended).map(([key, value]) => (
                    <View key={key} style={styles.requirementRow}>
                      <Text style={styles.requirementLabel}>{key.toUpperCase()}</Text>
                      <Text style={styles.requirementValue}>{value}</Text>
                    </View>
                  ))}
                </>
              )}
            </View>
          </View>
        )}

        {/* Arena-specific: Classes */}
        {game.arenaConfig?.classes && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}><MaterialCommunityIcons name="sword-cross" size={16} color="#FCD000" /> Playable Classes</Text>
            <Text style={styles.sectionSubtitle}>
              {game.arenaConfig.launchRoster || game.arenaConfig.classes.length} classes at launch
              {game.arenaConfig.fullRoster && ` • ${game.arenaConfig.fullRoster} planned`}
            </Text>
            <View style={styles.classesGrid}>
              {game.arenaConfig.classes.map((cls, i) => (
                <View key={i} style={styles.classTag}>
                  <Text style={styles.classText}>{cls}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Publisher Info */}
        <View style={styles.section}>
          <View style={styles.publisherRow}>
            <View style={styles.publisherInfo}>
              <Text style={styles.publisherLabel}>Developer</Text>
              <Text style={styles.publisherValue}>{game.developer || 'CritCash'}</Text>
            </View>
            <View style={styles.publisherInfo}>
              <Text style={styles.publisherLabel}>Publisher</Text>
              <Text style={styles.publisherValue}>{game.publisher || 'CritCash'}</Text>
            </View>
          </View>
        </View>
      </ScrollView>

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0b0b0b',
  },
  scrollView: {
    flex: 1,
  },
  content: {
    paddingBottom: 120,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#888',
    marginTop: 12,
    fontSize: 14,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  errorEmoji: {
    fontSize: 64,
    marginBottom: 16,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 20,
  },

  // Back Link
  backLink: {
    padding: 12,
    paddingBottom: 8,
  },
  backLinkText: {
    color: '#888',
    fontSize: 14,
  },

  // Hero Section
  heroSection: {
    marginBottom: 16,
  },
  coverContainer: {
    width: '100%',
    height: 200,
    backgroundColor: '#1a1a1a',
    position: 'relative',
  },
  coverImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  placeholderCover: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#1a1a1a',
  },
  placeholderText: {
    fontSize: 72,
    color: '#333',
    fontWeight: 'bold',
  },
  integrationBadge: {
    position: 'absolute',
    top: 12,
    right: 12,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  integrationBadgeText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '600',
  },
  firstPartyBanner: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(252, 208, 0, 0.95)',
    paddingVertical: 8,
    alignItems: 'center',
  },
  firstPartyText: {
    color: '#000',
    fontSize: 13,
    fontWeight: '700',
  },
  heroInfo: {
    padding: 12,
    paddingTop: 12,
  },
  gameTitle: {
    fontSize: 24,
    fontWeight: '900',
    color: '#fff',
    marginBottom: 4,
  },
  developer: {
    fontSize: 14,
    color: '#888',
    marginBottom: 12,
  },
  statsRow: {
    flexDirection: 'row',
    gap: 20,
  },
  statItem: {
    alignItems: 'flex-start',
  },
  statValue: {
    fontSize: 16,
    fontWeight: '700',
    color: '#fff',
  },
  statLabel: {
    fontSize: 11,
    color: '#666',
    marginTop: 2,
  },
  freeLabel: {
    color: '#22c55e',
  },

  // Action Section
  actionSection: {
    paddingHorizontal: 12,
    marginBottom: 24,
  },
  primaryButton: {
    backgroundColor: '#FCD000',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  playButton: {
    backgroundColor: '#22c55e',
  },
  downloadButtonStyle: {
    backgroundColor: '#2563eb',
  },
  installingButton: {
    backgroundColor: '#444',
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  primaryButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#000',
  },
  downloadingContainer: {
    backgroundColor: '#111',
    padding: 16,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#222',
  },
  progressBar: {
    height: 8,
    backgroundColor: '#222',
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 8,
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#2563eb',
    borderRadius: 4,
  },
  progressText: {
    fontSize: 13,
    color: '#888',
    textAlign: 'center',
  },
  secondaryActions: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 16,
    marginTop: 12,
  },
  secondaryButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  secondaryButtonText: {
    color: '#888',
    fontSize: 13,
  },
  backButton: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    backgroundColor: '#222',
    borderRadius: 8,
  },
  backButtonText: {
    color: '#fff',
    fontSize: 14,
  },

  // Sections
  section: {
    paddingHorizontal: 12,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '900',
    color: '#fff',
    marginBottom: 8,
  },
  sectionSubtitle: {
    fontSize: 12,
    color: '#666',
    marginBottom: 12,
  },
  description: {
    fontSize: 14,
    color: '#bbb',
    lineHeight: 22,
  },

  // Screenshots
  screenshotContainer: {
    paddingRight: 12,
  },
  screenshot: {
    width: SCREEN_WIDTH - 40,
    height: 160,
    borderRadius: 12,
    marginRight: 12,
    backgroundColor: '#1a1a1a',
  },
  dotsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 12,
    gap: 8,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#333',
  },
  dotActive: {
    backgroundColor: '#FCD000',
  },

  // Meta Grid
  metaGrid: {
    backgroundColor: '#0f0f0f',
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,
    borderColor: '#1a1a1a',
  },
  metaItem: {
    marginBottom: 14,
  },
  metaLabel: {
    fontSize: 11,
    color: '#666',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  metaValue: {
    fontSize: 14,
    color: '#fff',
  },
  tagContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tag: {
    backgroundColor: '#1a1a1a',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
  },
  platformTag: {
    backgroundColor: '#1a2a3a',
  },
  tagText: {
    fontSize: 12,
    color: '#ccc',
  },

  // Formats Grid
  formatsGrid: {
    gap: 10,
  },
  formatCard: {
    backgroundColor: '#0f0f0f',
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,
    borderColor: '#1a1a1a',
  },
  formatName: {
    fontSize: 15,
    fontWeight: '700',
    color: '#FCD000',
    marginBottom: 4,
  },
  formatDescription: {
    fontSize: 12,
    color: '#888',
    marginBottom: 10,
  },
  formatMeta: {
    flexDirection: 'row',
    gap: 14,
  },
  formatMetaText: {
    fontSize: 11,
    color: '#666',
  },

  // Requirements
  requirementsTable: {
    backgroundColor: '#0f0f0f',
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,
    borderColor: '#1a1a1a',
  },
  requirementsHeader: {
    marginBottom: 10,
  },
  requirementsHeaderText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#FCD000',
  },
  requirementRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#1a1a1a',
  },
  requirementLabel: {
    width: 80,
    fontSize: 11,
    color: '#666',
    textTransform: 'uppercase',
  },
  requirementValue: {
    flex: 1,
    fontSize: 12,
    color: '#ccc',
  },

  // Classes Grid
  classesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  classTag: {
    backgroundColor: '#1a1a2e',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#2a2a4a',
  },
  classText: {
    fontSize: 12,
    color: '#9ca3af',
    fontWeight: '500',
  },

  // Publisher Row
  publisherRow: {
    flexDirection: 'row',
    gap: 24,
    backgroundColor: '#0f0f0f',
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,
    borderColor: '#1a1a1a',
  },
  publisherInfo: {},
  publisherLabel: {
    fontSize: 10,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 4,
  },
  publisherValue: {
    fontSize: 13,
    color: '#fff',
  },
});